import React, { forwardRef, memo, useEffect, useMemo, useRef, useState, } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { m as motion, useMotionValue, useTransform } from 'framer-motion';
import { default as PolestarImage, } from '@polestar/component-warehouse-react/components/image/Image';
import Picture from '@polestar/component-warehouse-react/components/picture/Picture';
import { GallerySlideTypes, GalleryVariants, } from '@polestar/component-warehouse-react/components/gallery/Gallery';
import useResizeCallback from '@polestar/component-warehouse-react/hooks/useResizeCallback';
import { createTransition, MotionDurationsSeconds, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { MotionControlsVariants } from '@polestar/component-warehouse-react/components/motion/useMotionControls';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
import { useGallery } from '@polestar/component-warehouse-react/components/gallery/GalleryContext';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import VideoPlayer from '@polestar/component-warehouse-react/components/video-player/VideoPlayer';
import { calculateNewSlideIndex, convertIndexToSlideIndex } from '../Gallery.helpers';
import GalleryPauseButton from './GalleryPauseButton';
import useDimensionsCallback from '@polestar/component-warehouse-react/hooks/useDimensions';
import { useVideoPlayerAutoplay } from '@polestar/component-warehouse-react/hooks/useVideoPlayerAutoplay';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
const GallerySlides = memo(({ x, internalIndex, contentOffset, contentLoaded = false, CustomComponent, content, slideWidths, variant, initialized = true, percentages, dragged, isDark, ...props }) => {
    const xValue = useTransform(x, (latestX) => (percentages ? `${latestX}%` : `${latestX}px`));
    return (React.createElement(Container, { variant: variant, initial: false, style: {
            x: xValue,
        }, initialized: initialized, contentLoaded: contentLoaded, ...props }, content === null || content === void 0 ? void 0 : content.map((contentItemProps, index) => {
        var _a, _b;
        const filename = ((_b = (_a = contentItemProps === null || contentItemProps === void 0 ? void 0 : contentItemProps.sources) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.srcSet) || contentItemProps.src || '';
        const spacePosition = filename.lastIndexOf(' ');
        // this is needed to ensure that failing images will be updated when the content is changed outside the Gallery to refresh when loaded
        const keyIndex = `${index}-${filename.substring(filename.lastIndexOf('/') + 1, spacePosition !== -1 ? spacePosition : undefined)}`;
        return (React.createElement(GallerySlideWrapper, { key: keyIndex, x: x, contentLength: content.length / 2, index: index, internalIndex: internalIndex, percentages: percentages, variant: variant, contentItemProps: contentItemProps, slideWidths: slideWidths, dragged: dragged, contentOffset: contentOffset, CustomComponent: CustomComponent, isDark: isDark }));
    })));
});
GallerySlides.displayName = 'GallerySlides';
export default GallerySlides;
const GallerySlideWrapper = ({ index, internalIndex, x, contentLength, percentages, variant, contentOffset, CustomComponent, slideWidths, initialized = true, dragged, contentItemProps, isDark, }) => {
    const { isRTL } = useDirection();
    const slideRef = useRef(null);
    const videoSlideVideoRef = useRef(null);
    let slidesContent;
    const slideIndex = index < contentLength ? index : index - contentLength;
    const isFixedArrowVariant = variant === GalleryVariants.standard || variant === GalleryVariants.highContrastChevrons;
    /**
     * Calculate opacity of the peak overlay,
     */
    // TODO: unsure how to solve this best: contentLength is needed when moved outside this loop
    const opacity = useTransform(x, (latestX) => {
        if (!percentages) {
            return 0;
        }
        const xIndex = latestX / 100;
        const visibleSlideIndex = Math.round(-xIndex) % contentLength;
        const visibleSlide = visibleSlideIndex < 0 ? visibleSlideIndex + contentLength : visibleSlideIndex;
        return slideIndex === visibleSlide ? 0 : 1 - Math.abs(xIndex - Math.round(xIndex)) * 3;
    });
    let currentSlide = Math.trunc(x.get() / 100);
    let animating = false;
    let direction = 0;
    const transitionOffset = useTransform(x, (latestX) => {
        if (!percentages || variant !== GalleryVariants.standard) {
            return '0%';
        }
        const xIndex = latestX / 100;
        let currentIndex = -xIndex % contentLength;
        if (currentIndex <= 0) {
            currentIndex += contentLength;
        }
        if (currentIndex % 1 === 0) {
            // end of animation
            currentSlide = currentIndex;
            animating = false;
        }
        else if (!animating) {
            currentSlide = Math.trunc(currentIndex >= currentSlide ? currentIndex + 1 : currentIndex);
            direction = latestX;
            animating = true;
        }
        if (animating) {
            // determine direction
            const percentage = currentIndex - Math.trunc(currentIndex);
            if (direction !== 0 && direction !== 1 && direction !== -1) {
                // set direction
                direction = latestX > direction ? -1 : 1;
            }
            const newSlideIndex = calculateNewSlideIndex(direction, xIndex, contentLength);
            // add offset NOT to the upcoming slide
            if (index !== newSlideIndex) {
                return `${(direction < 0 ? 1 - percentage : percentage) * direction * 66.6}%`;
            }
        }
        // keep slide position to x=0 to overflow the previous slide
        return `0%`;
    });
    useEffect(() => {
        const slideElement = slideRef.current;
        if (!slideElement) {
            return;
        }
        slideElement.setAttribute('aria-hidden', index !== convertIndexToSlideIndex(internalIndex === null || internalIndex === void 0 ? void 0 : internalIndex.get(), contentLength, isRTL)
            ? 'true'
            : 'false');
        return internalIndex.on('change', (latestInternalIndex) => {
            slideElement.setAttribute('aria-hidden', index !== convertIndexToSlideIndex(latestInternalIndex, contentLength, isRTL)
                ? 'true'
                : 'false');
        });
    }, [index, internalIndex, contentLength, isRTL]);
    const stretchProps = contentItemProps.type === GallerySlideTypes.video
        ? {}
        : variant === GalleryVariants.contained
            ? {
                style: {
                    height: '100%',
                    width: 'auto',
                    maxWidth: 'none',
                },
            }
            : {
                stretch: true,
                objectFit: 'cover',
            };
    const customItem = React.isValidElement(contentItemProps);
    if (customItem) {
        const element = contentItemProps;
        const renderItem = React.cloneElement(element, {
            onClick: (e) => {
                var _a, _b;
                if (!dragged.current) {
                    (_b = (_a = element.props) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
                }
                else {
                    preventCancelableDefault(e);
                }
            },
        });
        slidesContent = (React.createElement(WrappedCustomSlide, { key: index, index: index, slideWidths: slideWidths, style: { x: isFixedArrowVariant ? transitionOffset : undefined } }, renderItem));
    }
    else if (CustomComponent) {
        const { style, ...customProps } = contentItemProps;
        // use a custom component to render the data inside the Gallery
        slidesContent = (React.createElement(CustomComponent, { key: index, ...customProps, style: {
                ...style,
                x: isFixedArrowVariant ? transitionOffset : undefined,
            } }));
    }
    else if (contentItemProps.type === GallerySlideTypes.video) {
        const { 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- ignored on purpose
        type, ...videoProps } = contentItemProps;
        const { style: stretchStyle } = stretchProps;
        slidesContent = (React.createElement(VideoSlide, { ref: videoSlideVideoRef, key: index, ...videoProps, contentLength: contentLength, index: index, style: {
                x: isFixedArrowVariant ? transitionOffset : undefined,
                ...stretchStyle,
            }, isDark: isDark }));
    }
    else if ((contentItemProps === null || contentItemProps === void 0 ? void 0 : contentItemProps.sources) !== undefined) {
        const { pictureProps, sources, source, style, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- ignore this to prevent a React warning to not propagate unsupported (custom) properties onto the DOM
        isDark, ...imgProps } = contentItemProps;
        const { style: stretchStyle, ...stretchWithoutStyleProps } = stretchProps;
        slidesContent = (React.createElement(Picture, { key: index, ...pictureProps, stretch: true }, sources === null || sources === void 0 ? void 0 :
            sources.map((source, sourceIndex) => React.createElement("source", { key: `source${sourceIndex}`, ...source })),
            React.createElement(WrappedImage, { index: index, slideWidths: slideWidths, objectPosition: contentItemProps.objectPosition || 'center center', src: source || '', variant: variant, initialized: initialized, ...imgProps, ...stretchWithoutStyleProps, style: {
                    ...style,
                    ...stretchStyle,
                    x: isFixedArrowVariant ? transitionOffset : undefined,
                } }),
            percentages && !isFixedArrowVariant && React.createElement(ImageOverlay, { style: { opacity } })));
    }
    else {
        const { style: imgStyle, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- ignore this to prevent a React warning to not propagate unsupported (custom) properties onto the DOM
        isDark, ...imgWithoutStyleProps } = contentItemProps;
        const { style: stretchStyle, ...stretchWithoutStyleProps } = stretchProps;
        slidesContent = (React.createElement(React.Fragment, { key: index },
            React.createElement(WrappedImage, { index: index, slideWidths: slideWidths, objectPosition: contentItemProps.objectPosition || 'center center', variant: variant, initialized: initialized, ...imgWithoutStyleProps, ...stretchWithoutStyleProps, style: {
                    ...imgStyle,
                    ...stretchStyle,
                    x: isFixedArrowVariant ? transitionOffset : undefined,
                } }),
            percentages && !isFixedArrowVariant && React.createElement(ImageOverlay, { style: { opacity } })));
    }
    if ((initialized || CustomComponent) && !customItem) {
        return (React.createElement(GallerySlide, { role: "tabpanel", ref: slideRef, key: index, index: index, contentOffset: contentOffset, initialized: initialized, variant: variant },
            slidesContent,
            percentages && !isFixedArrowVariant && React.createElement(ImageOverlay, { style: { opacity } })));
    }
    else {
        return slidesContent;
    }
};
const GallerySlide = forwardRef(({ index, contentOffset, initialized, variant, children }, ref) => {
    const internalOffset = useMotionValue(0);
    const zIndexOverride = useMotionValue(0);
    const { id: galleryId } = useGallery();
    // @ts-ignore
    const x = useTransform([internalOffset, contentOffset[index]], ([latestInternalOffset, latestContentOffset]) => `${latestContentOffset - latestInternalOffset}${variant === GalleryVariants.standard || variant === GalleryVariants.highContrastChevrons
        ? '%'
        : 'px'}`);
    const zIndex = useTransform([internalOffset, zIndexOverride], ([latestInternalOffset, latestzIndexOverride]) => {
        return latestzIndexOverride ? latestzIndexOverride : latestInternalOffset ? 2 : 1;
    });
    return (React.createElement(Slide, { id: `${galleryId}-${index}`, ref: ref, style: { x, zIndex }, initialized: initialized, variant: variant }, children));
});
GallerySlide.displayName = 'GallerySlide';
/**
 * @internal
 */
export const VideoSlide = forwardRef(({ index, contentLength, sources, tracks, style, isDark, ...props }, ref) => {
    var _a, _b;
    const videoHeight = useMotionValue(0);
    const internalRef = useRef(null);
    const [dimensionsRef, dimensions] = useDimensionsCallback();
    const combinedRef = useCombinedRef(ref, internalRef, dimensionsRef);
    const { internalIndex: galleryIndex } = useGallery();
    const [shouldAutoPlay] = useState(!!props.autoPlay && index === galleryIndex.get() % (contentLength * 2));
    const needsAutoplay = !!props.autoPlay && props.muted;
    const shouldDisplayNativeAutoplayVideo = needsAutoplay && !props.controls;
    const { play, pause, autoPlay } = useVideoPlayerAutoplay(internalRef);
    useEffect(() => {
        let timeout;
        const unsubscribe = galleryIndex.on('change', (value) => {
            const isActive = convertIndexToSlideIndex(value, contentLength) === index;
            if (shouldDisplayNativeAutoplayVideo && internalRef.current) {
                internalRef.current.currentTime = 0;
            }
            if (isActive && needsAutoplay) {
                play();
            }
            if (!isActive) {
                /*
                  Timeout is needed, this competes with the mouse up event from Video.
                  Dragging to next slide would otherwise result in the video being played again.
                 */
                timeout && clearTimeout(timeout);
                timeout = setTimeout(() => {
                    pause();
                }, 0);
            }
        });
        return () => {
            clearTimeout(timeout);
            unsubscribe();
        };
    }, [
        contentLength,
        galleryIndex,
        index,
        needsAutoplay,
        pause,
        play,
        shouldDisplayNativeAutoplayVideo,
    ]);
    useEffect(() => {
        videoHeight.set(dimensions.height);
    }, [dimensions.height, videoHeight]);
    // autoplay video at start
    useEffect(() => {
        if (internalRef.current && needsAutoplay) {
            if (shouldDisplayNativeAutoplayVideo) {
                internalRef.current.muted = true; // make sure video is muted for autoplay to work
            }
            autoPlay();
        }
    }, [autoPlay, needsAutoplay, shouldDisplayNativeAutoplayVideo]);
    // to ensure the video if stretched inside the Gallery correctly
    const videoElementStyle = {
        height: '100%',
        width: '100%',
        maxWidth: 'none',
        objectFit: 'cover',
    };
    return shouldDisplayNativeAutoplayVideo ? (React.createElement(React.Fragment, null,
        React.createElement(StyledVideo, { muted: true, playsInline: true, loop: true, ref: combinedRef, "data-autoplay": "true", poster: props.poster, style: videoElementStyle }, sources === null || sources === void 0 ? void 0 :
            sources.map((source, sourceIndex) => (React.createElement("source", { key: `source${sourceIndex}`, ...source }))),
            tracks &&
                (tracks === null || tracks === void 0 ? void 0 : tracks.map((subtitle, subtitleIndex) => (React.createElement("track", { key: `subtitle${subtitleIndex}`, ...subtitle }))))),
        React.createElement(GalleryPauseButton, { videoRef: internalRef, videoHeight: videoHeight, ariaLabels: props.ariaLabels
                ? {
                    play: (_a = props.ariaLabels) === null || _a === void 0 ? void 0 : _a.play,
                    pause: (_b = props.ariaLabels) === null || _b === void 0 ? void 0 : _b.pause,
                }
                : undefined, isDark: isDark }))) : (
    // @ts-ignore
    React.createElement(VideoPlayer, { ref: combinedRef, ...props, autoPlay: shouldAutoPlay, playsInline: true, containerProps: {
            as: motion.div,
            style: {
                pointerEvents: 'auto',
                width: '100%',
                ...style,
            },
        }, style: videoElementStyle }, sources === null || sources === void 0 ? void 0 :
        sources.map((source, sourceIndex) => React.createElement("source", { key: `source${sourceIndex}`, ...source })),
        tracks &&
            (tracks === null || tracks === void 0 ? void 0 : tracks.map((subtitle, subtitleIndex) => (React.createElement("track", { key: `subtitle${subtitleIndex}`, ...subtitle }))))));
});
const StyledVideo = styled.video `
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;
const customSlideVariants = {
    [MotionControlsVariants.enter]: {
        opacity: Opacities.none,
        x: Spacings.xLarge,
    },
    [MotionControlsVariants.visible]: {
        opacity: Opacities.full,
        x: 0,
        transition: createTransition({
            duration: MotionDurationsSeconds.xxSlow,
            ease: MotionEasings.easeOut,
        }),
    },
};
const WrappedCustomSlide = ({ index, slideWidths, ...props }) => {
    const callbackRef = useResizeCallback((node) => {
        if (node) {
            const newSlideWidths = [...slideWidths.get()];
            newSlideWidths[index] = node.offsetWidth;
            slideWidths.set(newSlideWidths);
        }
    }, [index, slideWidths]);
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(internalRef, callbackRef);
    const { isKeyboardControlled } = useInteractionMode();
    const { setIndex } = useGallery();
    useEffect(() => {
        const handleFocus = () => {
            setIndex(index);
        };
        const slideNode = internalRef.current;
        if (slideNode && isKeyboardControlled) {
            slideNode.addEventListener('focusin', handleFocus);
            return () => {
                slideNode.removeEventListener('focusin', handleFocus);
            };
        }
    }, [index, isKeyboardControlled, setIndex]);
    return React.createElement(CustomSlide, { ref: combinedRef, ...props, variants: customSlideVariants });
};
const WrappedImage = ({ index, slideWidths, variant, initialized, ...props }) => {
    const { name } = useMediaQuery();
    // TODO: get this from css somehow
    const spacing = useMemo(() => {
        return variant === GalleryVariants.contained
            ? name === DeviceTypes.mobile
                ? 8 // Spacings.xSmall
                : name === DeviceTypes.tablet
                    ? 12 // Spacings.small
                    : 16 // Spacings.medium (desktop)
            : 0;
    }, [name, variant]);
    const callbackRef = useResizeCallback((node) => {
        if (node) {
            const newSlideWidths = [...slideWidths.get()];
            newSlideWidths[index] = node.offsetWidth + spacing;
            slideWidths.set(newSlideWidths);
        }
    }, [index, slideWidths, spacing]);
    return (React.createElement(AnimatedPolestarImage, { ref: callbackRef, initialized: initialized, style: {
            minWidth: variant !== GalleryVariants.standard ? '100%' : undefined,
        }, ...props }));
};
const AnimatedPolestarImage = styled(PolestarImage, shouldNotForwardProps(false, 'initialized'))(({ initialized }) => !initialized &&
    css `
      flex-shrink: 0;
      ${responsiveStyles({
        marginInlineEnd: {
            mobile: Spacings.medium,
            tablet: Spacings.large,
        },
    })}
    `);
const Container = styled(motion.div, shouldNotForwardProps(false, 'initialized', 'variant', 'contentLoaded'))(({ variant, initialized, contentLoaded }) => css(css `
      height: 100%;
      flex: 1;
      pointer-events: none;
    `, !contentLoaded &&
    css `
        opacity: 0;
      `, contentLoaded &&
    css `
        opacity: 1;
      `, variant === GalleryVariants.contained &&
    css `
        flex: 0;
      `, !initialized &&
    css `
        display: flex;
        flex-wrap: nowrap;
      `));
const Slide = styled(motion.figure, shouldNotForwardProps(false, 'variant', 'initialized'))(({ initialized, variant }) => css(css `
      height: 100%;
      width: 100%;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      position: relative;

      > * {
        pointer-events: none;
      }
    `, initialized &&
    css `
        position: absolute;
      `, variant === GalleryVariants.contained &&
    css `
        display: inline-block;
        pointer-events: none;

        img {
          user-select: none;
          pointer-events: auto;
          -webkit-user-drag: none;
        }
      `));
const CustomSlide = styled(motion.div) `
  pointer-events: none;
  flex-shrink: 0;

  ${responsiveStyles({
    width: {
        mobile: '85%',
        tablet: '45%',
        desktop: '40%',
    },
    maxWidth: {
        mobile: '316px', // added the 16px
        tablet: '352px', // added the 32px
        desktop: '480px',
        desktopWide: '682px', // added the 32px
    },
    paddingInlineEnd: {
        mobile: Spacings.medium,
        tablet: Spacings.xLarge,
    },
})};

  &:last-of-type {
    ${responsiveStyles({
    width: {
        mobile: `calc(85% - ${Spacings.medium})`,
        tablet: `calc(45% - ${Spacings.xLarge})`,
        desktop: `calc(40% - ${Spacings.xLarge})`,
    },
    maxWidth: {
        mobile: `calc(316px - ${Spacings.medium})`,
        tablet: `calc(352px - ${Spacings.xLarge})`,
        desktop: `calc(480px - ${Spacings.xLarge})`,
        desktopWide: `calc(682px - ${Spacings.xLarge})`,
    },
})};

    padding-inline-end: 0;
  }

  > * {
    user-select: none;
    pointer-events: auto;
    -webkit-user-drag: none;
  }
`;
const ImageOverlay = styled(motion.div)(({ theme }) => css `
    position: absolute;
    inset-inline-start: 0;
    inset-inline-end: 0;
    inset-block-start: 0;
    inset-block-end: 0;
    background-color: ${theme.gallery.peakOverlayColor};
  `);
